import { SelectOption, useNotify } from '@globaleris/ui.library'
import { Button, makeStyles, useTheme } from '@material-ui/core'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  InvoiceConceptModel,
  InvoiceDetailResponseModel
} from '../../../api/Argentia/endpoints/invoices/model/invoices'
import { ProductConceptModel } from '../../../api/Argentia/model/contracts'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { InvoiceForm, InvoiceFormModel } from '../components/invoiceForm/invoiceForm'
import { mapInvoiceDetailToFormModel, mapProductsToSelectOptions } from '../invoicePage.mapper'
import { fetchAddConceptSelectOptions, fetchManualInvoiceDetails } from '../invoicesPage.service'
import { styles } from '../invoicesPage.styles'
import {
  InvoiceConceptRow,
  InvoiceConceptsTable,
  PERCENTAGE_CALC_TYPES
} from './components/invoiceConceptsTable/invoiceConceptsTable'
import { InvoiceSummarySection } from './components/invoiceSummarySection/invoiceSummarySection'

interface InvoiceEditPageProps {
  location?: {
    state: {
      invoiceData: InvoiceDetailResponseModel
    }
  }
}

export const InvoiceEditPage: React.FC<InvoiceEditPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'invoiceEditPage'])
  const { info, error } = useNotify()
  const history = useHistory()

  const invoiceData = props.location?.state?.invoiceData

  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetailResponseModel | undefined>(
    invoiceData
  )
  const [formData, setFormData] = useState<InvoiceFormModel>(
    invoiceData ? mapInvoiceDetailToFormModel(invoiceData) : ({} as InvoiceFormModel)
  )
  const [productsSelectOptions, setProductsSelectOptions] = useState<SelectOption[]>([])
  const [conceptsList, setConceptsList] = useState<ProductConceptModel[]>([])

  const [formIsValid, setFormIsValid] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [regularConceptRows, setRegularConceptRows] = useState<InvoiceConceptRow[]>([])
  const [percentageConceptRows, setPercentageConceptRows] = useState<InvoiceConceptRow[]>([])
  const [conceptsDirty, setConceptsDirty] = useState<boolean>(false)

  // Refs to always access latest rows inside useCallback without stale closures
  const regularConceptRowsRef = useRef<InvoiceConceptRow[]>([])
  const percentageConceptRowsRef = useRef<InvoiceConceptRow[]>([])
  regularConceptRowsRef.current = regularConceptRows
  percentageConceptRowsRef.current = percentageConceptRows

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  // #region Handlers Starts

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleSave = async () => {
    const clean = (rows: InvoiceConceptRow[]) =>
      rows.map(({ dirty, ...concept }) => ({
        ...concept,
        conceptId: concept.conceptId < 0 ? 0 : concept.conceptId
      }))
    const conceptsToSave = [...clean(regularConceptRows), ...clean(percentageConceptRows)]
    // TODO: call save endpoint with formData and conceptsToSave
  }

  const handleFormChange = (formState: InvoiceFormModel, formIsCompleted: boolean) => {
    setFormData(formState)
    setFormIsValid(formIsCompleted)
  }

  // ─── Concepts CRUD ──────────────────────────────────────────────────────────

  // TODO: replace stub with actual recalculate endpoint when defined
  // When the BE responds with updated concepts, merge by conceptId preserving dirty flags.
  // New rows (conceptId < 0) cannot be matched by the BE — they stay untouched.
  const recalculate = useCallback((allConcepts: InvoiceConceptRow[]) => {
    // const cleanConcepts = allConcepts.map(({ dirty, ...c }) => ({
    //   ...c,
    //   conceptId: c.conceptId < 0 ? 0 : c.conceptId
    // }))
    // callRecalculateEndpoint(cleanConcepts).then(response => {
    //   if (!response.success || !response.data) return
    //
    //   // Update summary
    //   setInvoiceDetail(prev => prev ? { ...prev, invoiceSummary: response.data.invoiceSummary } : prev)
    //
    //   // Merge: preserve dirty flag, only update calculated fields from BE
    //   const merge = (rows: InvoiceConceptRow[], updated: InvoiceConceptModel[]) =>
    //     rows.map(row => {
    //       if (row.conceptId < 0) return row  // new unsaved row — BE can't match it
    //       const fresh = updated.find(u => u.conceptId === row.conceptId)
    //       return fresh ? { ...fresh, dirty: row.dirty } : row
    //     })
    //
    //   setRegularConceptRows(prev => merge(prev, response.data.concepts))
    //   setPercentageConceptRows(prev => merge(prev, response.data.concepts))
    // })
  }, [])

  const handleRegularConceptAdd = useCallback(
    (concept: Omit<InvoiceConceptModel, 'id'>) => {
      const newRow: InvoiceConceptRow = { ...concept, conceptId: -Date.now(), dirty: true }
      setRegularConceptRows(prev => {
        const updated = [newRow, ...prev]
        recalculate([...updated, ...percentageConceptRowsRef.current])
        return updated
      })
      setConceptsDirty(true)
    },
    [recalculate]
  )

  const handleRegularConceptEdit = useCallback(
    (concept: InvoiceConceptRow) => {
      setRegularConceptRows(prev => {
        const updated = prev.map(c =>
          c.conceptId === concept.conceptId ? { ...concept, dirty: true } : c
        )
        recalculate([...updated, ...percentageConceptRowsRef.current])
        return updated
      })
      setConceptsDirty(true)
    },
    [recalculate]
  )

  const handleRegularConceptDelete = useCallback(
    (conceptId: number) => {
      setRegularConceptRows(prev => {
        const updated = prev.filter(c => c.conceptId !== conceptId)
        recalculate([...updated, ...percentageConceptRowsRef.current])
        return updated
      })
      setConceptsDirty(true)
    },
    [recalculate]
  )

  const handlePercentageConceptAdd = useCallback(
    (concept: Omit<InvoiceConceptModel, 'id'>) => {
      const newRow: InvoiceConceptRow = { ...concept, conceptId: -Date.now(), dirty: true }
      setPercentageConceptRows(prev => {
        const updated = [newRow, ...prev]
        recalculate([...regularConceptRowsRef.current, ...updated])
        return updated
      })
      setConceptsDirty(true)
    },
    [recalculate]
  )

  const handlePercentageConceptEdit = useCallback(
    (concept: InvoiceConceptRow) => {
      setPercentageConceptRows(prev => {
        const updated = prev.map(c =>
          c.conceptId === concept.conceptId ? { ...concept, dirty: true } : c
        )
        recalculate([...regularConceptRowsRef.current, ...updated])
        return updated
      })
      setConceptsDirty(true)
    },
    [recalculate]
  )

  const handlePercentageConceptDelete = useCallback(
    (conceptId: number) => {
      setPercentageConceptRows(prev => {
        const updated = prev.filter(c => c.conceptId !== conceptId)
        recalculate([...regularConceptRowsRef.current, ...updated])
        return updated
      })
      setConceptsDirty(true)
    },
    [recalculate]
  )

  // #endregion Handlers Ends

  // #region Hooks Starts

  const loadInvoiceData = useCallback(async () => {
    if (!invoiceData?.id) return
    setLoading(true)
    fetchManualInvoiceDetails(invoiceData.id).then(response => {
      if (response.success && response.data) {
        setInvoiceDetail(response.data)
        setFormData(mapInvoiceDetailToFormModel(response.data))
        const allConcepts = response.data.invoiceConcepts ?? []
        setRegularConceptRows(
          allConcepts.filter(c => !PERCENTAGE_CALC_TYPES.has(c.calculationType))
        )
        setPercentageConceptRows(
          allConcepts.filter(c => PERCENTAGE_CALC_TYPES.has(c.calculationType))
        )
        const products = response.data.products ?? []
        setProductsSelectOptions(mapProductsToSelectOptions(products))

        const productIds = products.map(p => p.id).filter((id): id is number => id != null)
        if (productIds.length > 0) {
          fetchAddConceptSelectOptions(productIds).then(conceptsResponse => {
            if (conceptsResponse.success && conceptsResponse.data) {
              setConceptsList(conceptsResponse.data)
            } else {
              error(t('invoiceEditPage:notifications.error.fetchConcepts'))
            }
          })
        }
      } else {
        error(t('invoiceEditPage:notifications.error.fetchDetails'))
      }
      setLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleReset = useCallback(async () => {
    setConceptsDirty(false)
    await loadInvoiceData()
  }, [loadInvoiceData])

  useEffect(() => {
    loadInvoiceData()
  }, [loadInvoiceData])

  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <PageHeader title={t('invoiceEditPage:header.title')}>
          <Button variant="text" color="primary" size="small" onClick={handleBackButtonClick}>
            {t('invoiceEditPage:actions.back')}
          </Button>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={handleReset}
            disabled={loading}
          >
            {t('invoiceEditPage:actions.reset', 'Restablecer')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            disabled={!formIsValid}
          >
            {t('invoiceEditPage:actions.save')}
          </Button>
        </PageHeader>

        <InvoiceForm
          invoiceFormData={loading ? undefined : formData}
          editMode={!!invoiceDetail?.id}
          onFormChange={handleFormChange}
          productsSelectOptions={productsSelectOptions}
        />

        <InvoiceSummarySection summary={invoiceDetail?.invoiceSummary} isDirty={conceptsDirty} />

        <InvoiceConceptsTable
          loading={loading}
          concepts={regularConceptRows}
          conceptsList={conceptsList}
          defaultStartDate={invoiceDetail?.startDate}
          defaultEndDate={invoiceDetail?.endDate}
          isPercentage={false}
          title={t('invoiceEditPage:concepts.tableTitle')}
          onAdd={handleRegularConceptAdd}
          onEdit={handleRegularConceptEdit}
          onDelete={handleRegularConceptDelete}
        />

        <InvoiceConceptsTable
          loading={loading}
          concepts={percentageConceptRows}
          conceptsList={conceptsList}
          defaultStartDate={invoiceDetail?.startDate}
          defaultEndDate={invoiceDetail?.endDate}
          isPercentage={true}
          title={t('invoiceEditPage:concepts.percentageTableTitle')}
          onAdd={handlePercentageConceptAdd}
          onEdit={handlePercentageConceptEdit}
          onDelete={handlePercentageConceptDelete}
        />
      </PageCenterColumn>
    </Page>
  )
}
